<template>
    <div class="view">
        <h2 v-if="mode === 'CREATE'">Créer entreprise / marché infos</h2>
        <h2 v-if="mode === 'UPDATE'">Modifier entreprise / marché infos</h2>

        <b-form @submit="onSubmit" @reset="onReset" v-if="show" v-bind:validated="formSubmitted">

            <b-col cols="10">

                <b-row align-h="end">
                    <b-col cols="3"><span class="typo-orange">* </span>{{ libelles.champsObligatoires }}</b-col>
                </b-row>

                <b-card header="Entreprise / Marché" class="mb-2">

                    <b-row>
                        <b-col cols="4">
                            <b-form-group id="input-group-marches"
                                          label="Marché" label-for="input-marches"
                                          label-class="typo-required-field">
                                <AutocompleteCustom
                                  id="input-marches"
                                  :items="marcheIdsItems"
                                  v-model="marcheId"
                                  @search="onMarcheSearch"
                                  :required="true"
                                  :placeholder=libelles.selectionnerMarche
                                >
                              </AutocompleteCustom>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col cols="9">
                            <b-form-group id="input-group-entrepriseId"
                                          :label="'SIRET / Raison Sociale Entreprise'"
                                          label-for="input-entrepriseId"
                                          label-class="typo-required-field">
                                <AutocompleteCustom
                                        id="input-entrepriseId"
                                        :items="entreprisesSiret"
                                        v-model="entrepriseId"
                                        @search="onSiretSearch"
                                        :required="true"
                                        :formatter="formatSiretSiren"
                                        :extra-option="'Soumettre une nouvelle entreprise'"
                                        @extraOptionClicked="onExtraOptionClicked">
                                </AutocompleteCustom>
                                <div class="typo-warning pt-1" v-if="formSubmitted && !entrepriseId">
                                    Veuillez entrer une entreprise faisant partie du référentiel
                                </div>
                            </b-form-group>
                        </b-col>
                        <b-col cols="3">
                            <b-button type="button" variant="primary" @click="showMarchesAssocies" v-if="entrepriseId">
                                Marchés associés
                            </b-button>
                        </b-col>
                    </b-row>
                </b-card>

                <b-card header="Caractéristiques de l'entreprise" class="mb-2">
                    <b-row>
                        <b-col cols="6">
                            <b-form-group id="input-group-pme" label="PME (au titre de la recommandation 2003/361/CE)"
                                          label-for="input-pme"
                                          label-class="typo-required-field">
                                <b-form-radio-group
                                        id="input-pme"
                                        v-model="infos.pme" :buttons="true"
                                        button-variant="outline-primary"
                                        required>
                                    <b-form-radio :value="true">Oui</b-form-radio>
                                    <b-form-radio :value="false">Non</b-form-radio>
                                </b-form-radio-group>
                            </b-form-group>
                        </b-col>
                        <b-col cols="3">
                            <b-form-group id="input-group-francilienne" label="Siège en IdF"
                                          label-for="input-francilienne"
                                          label-class="typo-required-field">
                                <b-form-radio-group
                                        id="input-francilienne"
                                        v-model="infos.francilienne" :buttons="true"
                                        button-variant="outline-primary"
                                        required>
                                    <b-form-radio :value="true">Oui</b-form-radio>
                                    <b-form-radio :value="false">Non</b-form-radio>
                                </b-form-radio-group>
                            </b-form-group>
                        </b-col>
                        <b-col cols="3">
                            <b-form-group id="input-group-agenceIdf" label="Agence en IdF"
                                          label-for="input-agenceIdf"
                                          label-class="typo-required-field">
                                <b-form-radio-group
                                        id="input-agenceIdf"
                                        v-model="infos.agenceIdf" :buttons="true"
                                        button-variant="outline-primary"
                                        required>
                                    <b-form-radio :value="true">Oui</b-form-radio>
                                    <b-form-radio :value="false">Non</b-form-radio>
                                </b-form-radio-group>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col>
                            <b-form-group id="input-group-chiffreAffaire" label="Chiffre d'affaires (en euros)"
                                          label-for="input-chiffreAffaire" label-class="typo-required-field">
                                <b-form-input
                                        id="input-chiffreAffaire"
                                        v-model="infos.chiffreAffaire"
                                        type="number"
                                        min="0"
                                        step="any"
                                        required>
                                </b-form-input>
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group id="input-group-effectif" label="Effectif global" label-for="input-effectif"
                                          label-class="typo-required-field">
                                <b-form-input
                                        id="input-effectif"
                                        v-model="infos.effectif"
                                        type="number"
                                        min="0"
                                        required>
                                </b-form-input>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col>
                            <b-form-group id="input-group-anneeChiffreAffaire"
                                          label="Année de référence du chiffre d'affaire"
                                          label-for="input-anneeChiffreAffaire"
                                          label-class="typo-required-field">
                                <b-form-select
                                        id="input-anneeChiffreAffaire"
                                        v-model="infos.anneeChiffreAffaire"
                                        :options="annees"
                                        required>
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group id="input-group-anneeEffectif" label="Année de référence de l'effectif global"
                                          label-for="input-anneeEffectif" label-class="typo-required-field">
                                <b-form-select
                                        id="input-anneeEffectif"
                                        v-model="infos.anneeEffectif"
                                        :options="annees"
                                        required>
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col>
                            <b-form-group
                                id="input-group-bilanTotal"
                                label="Bilan total (en euros)"
                                label-for="input-bilanTotal">
                                <b-form-input
                                    id="input-bilanTotal"
                                    v-model="infos.bilanTotal"
                                    type="number"
                                    min="0"
                                    step="any">
                                </b-form-input>
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group
                                id="input-group-autonome"
                                label="Entreprise autonome"
                                label-for="input-autonome">
                                <b-form-radio-group
                                    id="input-autonome"
                                    v-model="infos.autonome" :buttons="true"
                                    button-variant="outline-primary">
                                    <b-form-radio :value="true">Oui</b-form-radio>
                                    <b-form-radio :value="false">Non</b-form-radio>
                                </b-form-radio-group>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-card>

                <div class="pt-2">
                    <b-button type="submit" variant="primary" @click="formSubmitted = true">Soumettre</b-button>
                    <b-button v-if="!saveFormEntrepriseMarcheInfosStore.state.active" type="reset" variant="danger">
                        Réinitialiser
                    </b-button>
                    <b-button type="button"
                              v-if="entrepriseMarcheInfosFound && !saveFormEntrepriseMarcheInfosStore.state.active"
                              v-b-modal="'modal-delete'"
                              variant="warning">
                        Supprimer
                    </b-button>
                    <b-modal
                            :id="'modal-delete'"
                            title="Suppression"
                            ok-title="Confirmer"
                            cancel-title="Annuler"
                            @ok="deleteInfos">
                        <p>Confirmez-vous la suppression de ces informations ?</p>
                    </b-modal>
                </div>

            </b-col>

        </b-form>
        <br/>
    </div>

</template>

<script>
  import EntrepriseStore from '@/stores/EntrepriseStore'
  import MarcheStore from '@/stores/MarcheStore'
  import SaveFormEntrepriseStore from "../../stores/SaveFormEntrepriseStore";
  import SaveFormEntrepriseMarcheInfosStore from "../../stores/SaveFormEntrepriseMarcheInfosStore";
  import EntrepriseMarcheInfosStore from '@/stores/EntrepriseMarcheInfosStore'
  import AutocompleteCustom from "../../components/AutocompleteCustom";
  import formatSiretSiren from "../../filters/formatSiretSiren";
  import EntrepriseMarcheInfosDto from "../../dtos/EntrepriseMarcheInfosDto";
  import {DEPARTEMENTS_IDF} from "../../constants/common";
  import {libelles} from '../../constants/libelles';

  export default {
    components: {AutocompleteCustom},
    data() {
      return {
        formSubmitted: false,
        show: true,
        entrepriseMarcheInfosFound: false,
        saveFormEntrepriseMarcheInfosStore: SaveFormEntrepriseMarcheInfosStore,
        infos: new EntrepriseMarcheInfosDto(),
        entrepriseId: null,
        marcheId: null,
        marches: [],
        entreprisesSiret: [],
        annees: [],
        marcheIdsItems: [],
        libelles : libelles,
      }
    },
    computed: {
      mode() {
        return this.entrepriseMarcheInfosFound ? 'UPDATE' : 'CREATE';
      },
      entreprise() {
        let entrepriseFound;
        if (this.entrepriseId &&
            this.entreprisesSiret &&
            this.entreprisesSiret.length > 0) {
          entrepriseFound = this.entreprisesSiret.find(
              (value) => value.value === this.entrepriseId
          );
        }
        return entrepriseFound ? entrepriseFound.entreprise : null;
      },
    },
    watch: {
      entrepriseId() {
        this.searchEntrepriseMarcheInfos();
      },
      marcheId(newMarcheId) {
        this.onMarcheSearch(newMarcheId, true);
        this.searchEntrepriseMarcheInfos();
      },
    },
    mounted() {
      const currentYear = new Date().getFullYear();
      for (let i = currentYear; i >= 2010; i--) {
        this.annees.push(i);
      }
      MarcheStore.getAll().then(marches => {
        this.marches = marches.map(marche => {
          return {text: `${marche.reference} (${marche.libelleCourt})`, value: marche.id};
        });
        this.marcheIdsItems= this.marches;
      });
      this.initInfos();
    },
    beforeRouteLeave(_to, _from, _next) {
      if (SaveFormEntrepriseMarcheInfosStore.state.active && SaveFormEntrepriseMarcheInfosStore.state.route.name !== _to.name) {
        SaveFormEntrepriseMarcheInfosStore.clear();
      }
      _next();
    },
    methods: {
      initInfos() {
        this.entrepriseId = null;
        this.marcheId = null;
        this.formSubmitted = false;
        this.entrepriseMarcheInfosFound = false;
        if (SaveFormEntrepriseStore.state.active) {
          this.infos = SaveFormEntrepriseStore.state.form.infos;
          this.marcheId = SaveFormEntrepriseStore.state.form.marcheId;
          const entreprise = SaveFormEntrepriseStore.state.data;
          this.entreprisesSiret = [{
            text: entreprise.siret + ' (' + entreprise.libelle + ')',
            value: entreprise.id
          }];
          this.entrepriseId = entreprise.id;
        } else if (SaveFormEntrepriseMarcheInfosStore.state.active) {
          this.marcheId = SaveFormEntrepriseMarcheInfosStore.state.form.marcheId;
          this.entrepriseId = SaveFormEntrepriseMarcheInfosStore.state.form.entrepriseId;
          this.setEntreprisesSiret(this.entrepriseId);
        }
        else {
          this.infos = new EntrepriseMarcheInfosDto();
        }
        this.show = false;
        this.$nextTick(() => {
          this.show = true
        });

      },
      async onSiretSearch(siret) {
        this.entreprisesSiret = await EntrepriseStore.getBySiretOrLibelle(siret, false, '', false, true);
      },
      setEntreprisesSiret(entrepriseId) {
        EntrepriseStore.get(entrepriseId).then(entreprise => {
          this.entreprisesSiret = EntrepriseStore.getEntrepriseAutocomplete(entreprise);
        });
      },
      searchEntrepriseMarcheInfos() {
        if (this.entrepriseId && this.marcheId && !SaveFormEntrepriseStore.state.active) {
          EntrepriseMarcheInfosStore.getByEntrepriseIdAndMarcheId(this.entrepriseId, this.marcheId).then(
              infos => {
                this.entrepriseMarcheInfosFound = !!infos;
                if (infos) {
                  this.infos = infos;
                } else {
                  this.infos = new EntrepriseMarcheInfosDto();
                  if (this.entreprise) {
                    this.infos.francilienne = this.entreprise.villeSiren ?
                        DEPARTEMENTS_IDF.includes(this.entreprise.villeSiren.departement.code) : false;
                    this.infos.agenceIdf = this.entreprise.villeSiret ?
                        DEPARTEMENTS_IDF.includes(this.entreprise.villeSiret.departement.code) : false;
                  }
                }
              }
          );
        }
        SaveFormEntrepriseStore.clear();
      },
      formatSiretSiren(value) {
        return formatSiretSiren(value);
      },
      onExtraOptionClicked() {
        SaveFormEntrepriseStore.state.active = true;
        SaveFormEntrepriseStore.state.route = this.$route;
        SaveFormEntrepriseStore.state.form = {infos: this.infos, marcheId: this.marcheId};
        window.scrollTo(0, 0);
        this.$router.push("/entreprises/creation");
      },
      onSubmit(evt) {
        evt.preventDefault();
        if (this.entrepriseId && this.marcheId) {
          if (this.mode === 'CREATE') {
            this.infos.entrepriseId = this.entrepriseId;
            this.infos.marcheId = this.marcheId;
            EntrepriseMarcheInfosStore.create(this.infos);
          } else {
            EntrepriseMarcheInfosStore.update(this.infos).then(value => {
              if (SaveFormEntrepriseMarcheInfosStore.state.active && value) {
                SaveFormEntrepriseMarcheInfosStore.state.data = value;
                window.scrollTo(0, 0);
                this.$router.push(SaveFormEntrepriseMarcheInfosStore.state.route.path);
              }
            });
          }
        }
      },
      onReset(evt) {
        evt.preventDefault();
        SaveFormEntrepriseStore.clear();
        this.initInfos();
      },
      deleteInfos() {
        EntrepriseMarcheInfosStore.delete(this.infos).then(() => {
          this.initInfos();
        });
      },
      showMarchesAssocies() {
        MarcheStore.getByEntrepriseIdAndValideEmpreinte(this.entrepriseId).then(marches => {
          const h = this.$createElement;
          const lis = [];
           for (const marche of marches) {
             lis.push(h("li", `${marche.reference} (${marche.libelleCourt})`));
           }
          const message = h("ul", lis);
          this.$bvModal.msgBoxOk([message], {
            title: "Marchés associés à cette entreprise (avec une empreinte valide)",
            okTitle: "Ok",
          });
        });
      },
      onMarcheSearch(marche, isId){
        this.marcheIdsItems = MarcheStore.onMarcheSearch(marche, isId, this.marches)
      }
    }
  }
</script>
